import { supabase } from './supabase';

const API_BASE = import.meta.env.VITE_API_URL || '';

export class UsageLimitError extends Error {
  tier: string;
  used: number;
  limit: number;
  periodEnd: string;

  constructor(message: string, tier: string, used: number, limit: number, periodEnd: string) {
    super(message);
    this.name = 'UsageLimitError';
    this.tier = tier;
    this.used = used;
    this.limit = limit;
    this.periodEnd = periodEnd;
  }
}

/**
 * Get the current auth token for API requests
 */
async function getAuthToken(): Promise<string | null> {
  if (!supabase) return null;

  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

/**
 * Make an authenticated API request
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 429 && errorData.upgradeUrl) {
      const err = new UsageLimitError(
        errorData.message || 'Usage limit exceeded',
        errorData.tier,
        errorData.used,
        errorData.limit,
        errorData.periodEnd,
      );
      throw err;
    }
    throw new Error(errorData.message || errorData.error || `Request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Scrape a URL
 */
export async function scrapeUrl(url: string, includeImages: boolean = true) {
  return apiRequest<{ url: string; title: string; markdown: string } | { pages: Array<{ url: string; title: string; markdown: string }>; count: number }>('/api/scrape', {
    method: 'POST',
    body: JSON.stringify({ url, includeImages }),
  });
}

/**
 * Get user's scraping history
 */
export async function getUserHistory() {
  return apiRequest<{ history: Array<{
    id: string;
    root_url: string;
    status: string;
    page_count: number;
    created_at: string;
  }> }>('/api/user/history');
}

/**
 * Get pages for a specific job
 */
export async function getJobPages(jobId: string) {
  return apiRequest<{ pages: Array<{
    id: string;
    url: string;
    title: string;
    markdown: string;
    created_at: string;
  }> }>(`/api/user/history/${jobId}`);
}

/**
 * Get user's usage stats
 */
export async function getUserStats() {
  return apiRequest<{
    totalScrapes: number;
    totalPages: number;
    periodStart: string;
  }>('/api/user/stats');
}
